import type { ResultOf } from '@graphql-typed-document-node/core'
import { NoImage } from 'macaron-frontend-ui'
import Image from '~/components/image/Image'
import { getFragmentData } from '~/lib/graphql'
import type { FragmentType } from '~/lib/graphql'
import type { RestaurantId } from '~/lib/graphql/scalars'
import { Link } from '~/navigation'
import { css, sva } from '~/styled-system/css'
import { hstack } from '~/styled-system/patterns'
import { RestaurantHighlights } from '../shared/RestaurantHighlight.frag'

type Props = {
  restaurant: FragmentType<typeof RestaurantHighlights>
}

export function Highlights({ restaurant }: Props) {
  const { restaurantId, highlights } = getFragmentData(RestaurantHighlights, restaurant)

  if (highlights.length === 0) {
    return null
  }

  if (highlights.length === 1) {
    return (
      <button type="button">
        <FullyDescriptiveHighlightCard restaurantId={restaurantId} highlight={highlights[0]} />
      </button>
    )
  }

  const highlightKeys = highlights.map((_, index) => `${restaurantId}-${index}`)

  if (highlights.length === 2) {
    return (
      <div
        className={hstack({
          gap: '5',
        })}
      >
        {highlights.map((highlight, index) => (
          <button type="button" className={css({ flex: '1' })} key={highlightKeys[index]}>
            <HorizontalHighlightCard restaurantId={restaurantId} highlight={highlight} />
          </button>
        ))}
      </div>
    )
  }

  return (
    <div
      className={hstack({
        gap: '5',
        alignItems: 'stretch',
      })}
    >
      {highlights.map((highlight, index) => (
        <button type="button" className={css({ flex: '1' })} key={highlightKeys[index]}>
          <VerticalHighlightCard restaurantId={restaurantId} highlight={highlight} />
        </button>
      ))}
    </div>
  )
}

type HighlightProps = {
  restaurantId: RestaurantId
  highlight: ResultOf<typeof RestaurantHighlights>['highlights'][number]
}

const styles = sva({
  slots: ['hcard', 'vcard', 'description', 'title', 'text', 'more'],
  base: {
    hcard: {
      display: 'flex',
      gap: '0',
      border: 'medium',
      borderColor: 'gray.300',
      rounded: 'default',
      overflow: 'hidden',
    },
    vcard: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0',
      border: 'medium',
      borderColor: 'gray.300',
      rounded: 'default',
      overflow: 'hidden',
      h: 'full',
    },
    description: {
      display: 'flex',
      flexDirection: 'column',
      flexGrow: '1',
      p: '4',
      textAlign: 'left',
    },
    title: {
      fontWeight: 'bold',
      lineClamp: '3',
    },
    more: {
      color: 'link',
      mt: 'auto',
      pt: '2',
      fontSize: 'sm',
    },
  },
})

function FullyDescriptiveHighlightCard({ restaurantId, highlight }: HighlightProps) {
  const classes = styles()

  return (
    <section>
      <Link
        to={`/${restaurantId}/highlights`}
        preventScrollReset={true}
        prefetch="viewport"
        className={classes.hcard}
      >
        <HighlightSquareImage highlight={highlight} width={160} />
        <div className={classes.description}>
          <h3 className={classes.title}>{highlight.title}</h3>
          <p className={css({ mt: '2', lineClamp: '3' })}>{highlight.description}</p>
          <p className={classes.more}>続きをみる</p>
        </div>
      </Link>
    </section>
  )
}

function HorizontalHighlightCard({ restaurantId, highlight }: HighlightProps) {
  const classes = styles()

  return (
    <section>
      <Link
        to={`/${restaurantId}/highlights`}
        preventScrollReset={true}
        prefetch="viewport"
        className={classes.hcard}
      >
        <HighlightSquareImage highlight={highlight} width={120} />
        <div className={classes.description}>
          <h3 className={classes.title}>{highlight.title}</h3>
          <p className={classes.more}>続きをみる</p>
        </div>
      </Link>
    </section>
  )
}

function VerticalHighlightCard({ restaurantId, highlight }: HighlightProps) {
  const classes = styles()

  return (
    <section className={css({ height: 'full' })}>
      <Link
        to={`/${restaurantId}/highlights`}
        preventScrollReset={true}
        prefetch="viewport"
        className={classes.vcard}
      >
        <HighlightSquareImage highlight={highlight} width={180} width_full={true} />
        <div className={classes.description}>
          <h3 className={classes.title}>{highlight.title}</h3>
          <p className={classes.more}>続きをみる</p>
        </div>
      </Link>
    </section>
  )
}

function HighlightSquareImage({
  highlight,
  width,
  width_full = false,
}: Pick<HighlightProps, 'highlight'> & { width: number; width_full?: boolean }) {
  // TODO: Imageと一緒にPanda化する
  const className = width_full ? 'w-full' : ''

  if (highlight.image) {
    return (
      <Image
        src={highlight.image.url}
        alt={highlight.imageAlt}
        height={width}
        width={width}
        className={className}
      />
    )
  } else {
    return <NoImage height={width} width={width} />
  }
}
